/**
 * ObservationTaskSelector — Expandable sidebar for the CYP ASD Observation module.
 *
 * Always mounted in the DOM (never conditionally rendered) so that its
 * appearance/disappearance can be smoothly animated via a CSS width transition.
 *
 * Three width states:
 *   0px        — no tasks have been added yet (sidebar invisible)
 *   72px       — compact / default (badge-only strip; matches SSI ModularSideNavbar)
 *   260px      — expanded via the chevron toggle (badge + full task name)
 *
 * Displays:
 *   • A numbered badge for each added task (order-based: first added = 1).
 *   • A "+" badge to open the task picker and add more tasks.
 *   • A "Review" badge at the bottom (separated by a divider).
 *
 * Expand/collapse is a click on the chevron at the top of the strip — not hover
 * — so the clinician can keep the labels visible (or keep the strip compact)
 * while they work. That choice is stored in localStorage and restored on
 * refresh. While collapsed, each numbered badge still has a native tooltip
 * with the section name so the clinician can identify a tab without expanding.
 *
 * A confirmation dialog (with blur overlay) is shown before removing a task
 * to prevent accidental data loss.
 *
 * NOTE ON THE SHARED AUTISM NAV PRIMITIVES
 * The other Autism modules now render their sidebars via the shared
 * <ModularSideNavbar>/<ModularNavRow> (src/components/autismModular). This
 * selector intentionally stays bespoke: it has affordances the shared row does
 * not model (click-to-expand width animation, an ADOS-mode sidebar swap, the
 * criterion-mapping/ADOS toggle rows with their own colour schemes, and a
 * deliberately different badge precedence where "started" outranks "active").
 * Forcing it onto the generic row would change its look/behaviour, so per the
 * plan's guidance we keep the wrapper here. The Observation step FOOTER, by
 * contrast, now uses the shared <ModularStepFooter> (see AdultAsdObservation).
 */

import React, { useCallback, useState } from 'react';
import { XIcon, PlusIcon, ExclamationIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';
import { GENERAL_PANELS } from '../config/generalObservationsConfig';
import { DSM5_DOMAINS } from '../config/observationTasksConfig';

/** Shared react-tooltip id for collapsed-row name balloons (portaled, so overflow cannot clip them). */
const OBS_SIDEBAR_TAB_TOOLTIP_ID = 'obs-sidebar-tab';

/**
 * localStorage key for the Observation sidebar expanded/collapsed preference.
 * Versioned so a future shape change can bump the key rather than mis-read
 * a stale boolean. Shared across every Observation page that renders this
 * selector (Task Administration, Adult Observation, CYP Observation) so the
 * clinician's choice follows them through the module and survives refresh.
 */
export const OBS_SIDEBAR_EXPANDED_KEY = 'td.obs.v1.sidebarExpanded';

/** Collapsed is the first-visit default, matching the old compact strip. */
function readSidebarExpanded() {
  try {
    return JSON.parse(localStorage.getItem(OBS_SIDEBAR_EXPANDED_KEY)) === true;
  } catch {
    return false;
  }
}

function writeSidebarExpanded(expanded) {
  try {
    localStorage.setItem(OBS_SIDEBAR_EXPANDED_KEY, JSON.stringify(expanded));
  } catch {
    // private mode / quota — keep the in-memory choice for this visit
  }
}

export default function ObservationTaskSelector({
  tasks,
  selectedTaskIds,
  activeTaskId,
  activeView,
  assessmentMode = 'build',
  allTaskData = {},
  activeGeneralPanel = null,
  onSelectTask,
  onRemoveTask,
  onOpenPicker,
  onSelectSummary,
  onSelectGeneral,
  criterionMappingMode = false,
  onToggleCriterionMapping,
  adosMode = false,
  onToggleAdosMode,
  activeCriterionId = null,
  onSelectCriterion,
  showAdosSidebar = true,
  /** When false, hides the Review nav item (e.g. Task Administration uses footer CTA instead). */
  showReview,
  /** When true, hides the Sensory (B4) panel from General Observations. */
  hideSensoryPanel = false,
  /**
   * Sidebar structure variant:
   *   'default'   — tasks first, then workflow toggles / general panels / review.
   *   'synthesis' — Criterion Mapping task list (ADOS row only if onToggleAdosMode
   *                 is provided; the dedicated ADOS phase omits that handler).
   */
  sidebarLayout = 'default',
  /**
   * When true, Task Administration marks a task "complete" (green) as soon as
   * its general Observation Notes box has content — the live-capture signal of
   * the v2 tagged-notes redesign, where evidence decisions come later in
   * Criterion Mapping rather than per task. When false (v1 / default), Task
   * Administration keeps the pre-redesign behaviour of colouring by per-task
   * evidence decisions. Criterion Mapping mode ignores this — it always uses
   * the per-task evidence check.
   */
  useNotesCaptureSignal = false,
}) {
  /** In preset mode the task list is fixed — no add/remove UI needed. */
  const isPreset = assessmentMode === 'preset';

  // Click-to-toggle, persisted so a refresh does not snap the sidebar back
  // to compact. Hover no longer expands — that used to fight the clinician
  // who wanted the strip to stay out of the way (or stay labelled).
  const [isExpanded, setIsExpanded] = useState(readSidebarExpanded);
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      writeSidebarExpanded(next);
      return next;
    });
  }, []);

  /**
   * Collapsed-only hover name. Native `title` is delayed and gets clipped by
   * the scrolling strip, so we use the same react-tooltip balloons as the SSI
   * sidenav — they portal to the document and appear immediately to the right
   * of the numbered badge. Dropped when expanded because the label is visible.
   */
  const collapsedNavProps = (label) => (
    isExpanded
      ? {}
      : {
          'data-tooltip-id': OBS_SIDEBAR_TAB_TOOLTIP_ID,
          'data-tooltip-content': label,
          'aria-label': label,
        }
  );

  /**
   * Sidebar completion colours for each task row.
   *
   * Task Administration, v2 (useNotesCaptureSignal): a task turns green once the
   * clinician has captured at least one note in that task's general Observation
   * Notes box (`masterNotes`). That is the live-capture signal of the redesign,
   * where evidence decisions come later in Criterion Mapping — not per task.
   *
   * Task Administration, v1 (default) AND Criterion Mapping / synthesis layout:
   * the pre-redesign per-task evidence-decision check (every relevant + extra
   * criterion decided). v1 records evidence per task, so this is its true
   * completion signal in every phase.
   *
   * "Not administered" always wins — muted state, never green.
   */
  const getTaskStatus = (task) => {
    const data = allTaskData[task.id];
    if (!data) return { complete: false, started: false, notAdministered: false };

    // A task the clinician marked "Not administered" is neither complete nor in
    // progress — it is deliberately excluded, so it gets its own muted state.
    const notAdministered = Boolean(data.notAdministered);
    if (notAdministered) {
      return { complete: false, started: false, notAdministered: true };
    }

    // v2 Task Administration only: green when the general notes box has content.
    // v1 always falls through to the per-task evidence check below, matching the
    // pre-redesign behaviour in both Task Administration and Criterion Mapping.
    if (useNotesCaptureSignal && !criterionMappingMode) {
      const notes = Array.isArray(data.masterNotes) ? data.masterNotes : [];
      const hasNotes = notes.some((n) => n && String(n.text || '').trim());
      return {
        complete: hasNotes,
        started: hasNotes,
        notAdministered: false,
      };
    }

    // Criterion Mapping / synthesis (and v1 Task Administration): evidence
    // decisions per criterion.
    const extraCriteria = data.dsm_extraCriteria || [];
    const allCriteria = [
      ...(task.relevantCriteria || []),
      ...extraCriteria.filter(id => !(task.relevantCriteria || []).includes(id)),
    ];

    if (allCriteria.length === 0) return { complete: false, started: false, notAdministered: false };

    let filled = 0;
    for (const cId of allCriteria) {
      if (data[`dsm_${cId}_evidence`]) filled++;
    }

    return {
      complete: filled === allCriteria.length,
      started: filled > 0,
      notAdministered: false,
    };
  };

  /**
   * Badge colour logic — mirrors the SSI sidebar:
   *   • Complete → green (emerald), stays green even when active
   *   • Active (not complete) → brand primary
   *   • Started → light primary tint
   *   • Default → neutral grey
   *
   * Selection is shown via a ring rather than changing the fill colour
   * so the completion state always remains visible.
   */
  const badgeClasses = (isActive, isComplete, isStarted, isNotAdministered = false) => {
    const base = isNotAdministered
      ? 'bg-gray-100 text-gray-400 line-through'
      : isComplete
        ? 'bg-emerald-500 text-white'
        : isStarted
          ? 'bg-primary/10 text-primary'
          : isActive
            ? 'bg-primary text-white'
            : 'bg-gray-200 text-gray-500';

    const ring = isActive ? 'ring-2 ring-primary ring-offset-1' : '';
    return `${base} ${ring}`;
  };

  /**
   * Holds the task ID pending removal confirmation, or null when the dialog
   * is closed. Opening this dialog pauses all other sidebar interactions.
   */
  const [confirmRemoveTaskId, setConfirmRemoveTaskId] = useState(null);

  const isSummaryActive = activeView === 'summary';
  const isPickerActive = activeView === 'picker';
  const hasTasks = selectedTaskIds.length > 0;
  const isSynthesisLayout = sidebarLayout === 'synthesis';
  const shouldShowReview = showReview ?? !!onSelectSummary;
  const generalPanels = hideSensoryPanel
    ? GENERAL_PANELS.filter((panel) => panel.id !== 'sensory')
    : GENERAL_PANELS;
  const isAdosNavActive = activeView === 'ados';

  const addedTasks = selectedTaskIds.map((taskId, index) => ({
    displayNumber: index + 1,
    task: tasks.find(t => t.id === taskId),
  }));

  /** The task object pending removal (used to display its name in the dialog). */
  const pendingTask = confirmRemoveTaskId
    ? tasks.find(t => t.id === confirmRemoveTaskId)
    : null;

  /**
   * Resolve the current width class for the sidebar container.
   * When no tasks exist the sidebar collapses to 0, creating a smooth
   * slide-in/out when the first task is added or the last is removed.
   */
  // Match ModularSideNavbar compact strip (SSI Criteria): 72px + px-2 so a
  // centred w-9 badge keeps ring-2 + ring-offset-1 without clipping.
  const widthClasses = !hasTasks
    ? 'min-w-0 max-w-0 px-0 border-r-0 opacity-0'
    : isExpanded
      ? 'min-w-[260px] max-w-[260px] px-2.5 border-r opacity-100'
      : 'min-w-[72px] max-w-[72px] px-2 border-r opacity-100';

  // Collapsed rows are badge-only (no leftover gap for a hidden label) so the
  // strip centres like SSI. Expanded rows restore the label + gap.
  const rowLayoutClass = isExpanded
    ? 'gap-2 px-1.5 justify-start'
    : 'gap-0 px-1 justify-center';
  const labelVisibilityClass = isExpanded
    ? 'opacity-100'
    : 'opacity-0 w-0 max-w-0 overflow-hidden';
  const badgeSizeClass = 'w-9 h-7 rounded-md';

  return (
    <>
      {/*
        Height is bounded by the module's flex row (h-[calc(100vh-8rem)]). min-h-0
        lets the inner column shrink and scroll; without it, flex children grow to
        content height and overflow-hidden clips items below the fold.
      */}
      {/*
        Overflow stays visible on this wrapper so the seam handle (below) can
        sit half on the sidebar and half over the main panel. Vertical clipping
        is the inner list's job — if we hid overflow here, CSS would also clip
        the handle (setting overflow-y to hidden forces overflow-x to auto).
      */}
      <div
        className={`
          relative z-10 mt-6 border-gray-200 flex flex-col self-stretch min-h-0
          sticky top-8
          transition-all duration-300 ease-in-out
          ${isExpanded || !hasTasks ? 'items-stretch' : 'items-center'}
          ${widthClasses}
        `}
      >
        {/*
          Seam handle — same idea as the SSI Display Preferences / Functional
          Impairment hanging tab (a control attached to the panel edge, not a
          row in the nav list) and the carousel's edge chevrons. Vertically
          centred on the sidebar border so it never reads as another tab.
          Hover only shifts the icon colour (ghost secondary); no fill.
        */}
        {hasTasks && (
          <button
            type="button"
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            className="
              absolute top-1/2 -translate-y-1/2 -right-3 z-20
              flex items-center justify-center
              w-6 h-11 rounded-full
              bg-white border border-gray-200
              text-gray-400 hover:text-primary
              shadow-sm
              transition-colors duration-150
              focus:outline-none focus:ring-2 focus:ring-primary/30
            "
          >
            {isExpanded
              ? <ChevronLeftIcon className="w-4 h-4" aria-hidden="true" />
              : <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />}
          </button>
        )}
        <div className={`
          flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden flex flex-col gap-2
          ${isExpanded || !hasTasks ? '' : 'items-center'}
        `}>
        {/* ── ADOS Mode: show DSM criteria as primary navigation ───────── */}
        {adosMode && showAdosSidebar ? (
          <>
            {/* Section label */}
            <div className={`
              text-[9px] font-bold uppercase tracking-wider text-indigo-500 px-2 pb-1
              transition-opacity duration-200
              ${isExpanded ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}
            `}>
              DSM-5 Criteria
            </div>

            {DSM5_DOMAINS.map((domain) => {
              const isActive = activeView === 'ados' && activeCriterionId === domain.id;
              return (
                <div
                  key={domain.id}
                  onClick={() => onSelectCriterion?.(domain.id)}
                  className={`
                    flex items-center rounded-lg cursor-pointer
                    transition-all duration-200 w-full py-1.5
                    ${rowLayoutClass}
                    ${isActive ? 'bg-indigo-50' : 'hover:bg-gray-100'}
                  `}
                  role="button"
                  tabIndex={0}
                  {...collapsedNavProps(`${domain.id}. ${domain.shortTitle}`)}
                >
                  <div className={`
                    ${badgeSizeClass} flex items-center justify-center text-xs font-bold flex-shrink-0
                    transition-all duration-200
                    ${isActive
                      ? 'bg-indigo-600 text-white ring-2 ring-indigo-400 ring-offset-1'
                      : 'bg-gray-200 text-gray-500'}
                  `}>
                    {domain.id}
                  </div>

                  <span className={`
                    text-sm font-medium truncate whitespace-nowrap
                    transition-opacity duration-200
                    ${isActive ? 'text-gray-900 font-semibold' : 'text-gray-600'}
                    ${labelVisibilityClass}
                  `}>
                    {domain.shortTitle}
                  </span>
                </div>
              );
            })}

            {/* Divider + Back button */}
            <div className={`border-t border-gray-200 my-1 transition-all duration-300 ${isExpanded ? 'w-full' : 'w-8 mx-auto'}`} />

            <div
              onClick={onToggleAdosMode}
              className={`
                flex items-center rounded-lg cursor-pointer
                transition-all duration-200 w-full py-1.5 hover:bg-gray-100
                ${rowLayoutClass}
              `}
              role="button"
              tabIndex={0}
              {...collapsedNavProps('Return to criteria mapping view')}
            >
              <div className={`${badgeSizeClass} flex items-center justify-center flex-shrink-0 transition-all duration-200 bg-gray-200 text-gray-500 hover:bg-indigo-100 hover:text-indigo-600`}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
              </div>
              <span className={`
                text-sm font-medium truncate whitespace-nowrap text-gray-600
                transition-opacity duration-200
                ${labelVisibilityClass}
              `}>
                Back to Tasks
              </span>
            </div>
          </>
        ) : isSynthesisLayout ? (
        <>
        {/* ── Synthesis layout: ADOS first, then criterion-mapping tasks ── */}
        {/* ADOS row + divider only when this phase exposes an ADOS jump.
            Criterion Mapping omits onToggleAdosMode — without this guard a lone
            top divider sits above the task badges (looks broken in compact). */}
        {onToggleAdosMode && (
          <>
            <div
              onClick={onToggleAdosMode}
              className={`
                flex items-center rounded-lg cursor-pointer
                transition-all duration-200 w-full py-1.5
                ${rowLayoutClass}
                ${isAdosNavActive ? 'bg-indigo-50' : 'hover:bg-gray-100'}
              `}
              role="button"
              tabIndex={0}
              {...collapsedNavProps('ADOS-Informed Coding')}
            >
              <div className={`
                ${badgeSizeClass} flex items-center justify-center flex-shrink-0
                transition-all duration-200
                ${isAdosNavActive
                  ? 'bg-indigo-600 text-white ring-2 ring-indigo-400 ring-offset-1'
                  : 'bg-gray-200 text-gray-500 hover:bg-indigo-100 hover:text-indigo-600'}
              `}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>

              <span className={`
                text-sm font-medium truncate whitespace-nowrap
                transition-opacity duration-200
                ${isAdosNavActive ? 'text-indigo-700 font-semibold' : 'text-gray-600'}
                ${labelVisibilityClass}
              `}>
                ADOS-Informed Coding
              </span>
            </div>

            <div className={`border-t border-gray-200 my-1 transition-all duration-300 ${isExpanded ? 'w-full' : 'w-8 mx-auto'}`} />
          </>
        )}

        {addedTasks.map(({ displayNumber, task }) => {
          if (!task) return null;
          const isActive = activeTaskId === task.id && activeView === 'task';
          const { complete: isComplete, started: isStarted, notAdministered: isNotAdministered } = getTaskStatus(task);

          return (
            <div
              key={task.id}
              onClick={() => onSelectTask(task.id)}
              className={`
                relative group flex items-center rounded-lg cursor-pointer
                transition-all duration-200 w-full py-1.5
                ${rowLayoutClass}
                ${isActive && !isComplete ? 'bg-primary/10' : isComplete && isActive ? 'bg-emerald-50' : 'hover:bg-gray-100'}
              `}
              role="button"
              tabIndex={0}
              {...collapsedNavProps(`${task.shortTitle}${isNotAdministered ? ' — not administered' : isComplete ? ' — complete' : isStarted ? ' — in progress' : ''}`)}
            >
              <div className={`
                ${badgeSizeClass} flex items-center justify-center text-xs font-bold flex-shrink-0
                transition-all duration-200
                ${badgeClasses(isActive, isComplete, isStarted, isNotAdministered)}
              `}>
                {displayNumber}
              </div>

              <span className={`
                text-sm font-medium truncate whitespace-nowrap
                transition-opacity duration-200
                ${isNotAdministered ? 'text-gray-400 italic' : isComplete ? 'text-emerald-700 font-semibold' : isActive ? 'text-gray-900 font-semibold' : 'text-gray-600'}
                ${labelVisibilityClass}
              `}>
                {task.shortTitle}
              </span>
            </div>
          );
        })}
        </>
        ) : (
        <>
        {/* Added task badges */}
        {addedTasks.map(({ displayNumber, task }) => {
          if (!task) return null;
          const isActive = activeTaskId === task.id && activeView === 'task';
          const { complete: isComplete, started: isStarted, notAdministered: isNotAdministered } = getTaskStatus(task);

          return (
            <div
              key={task.id}
              onClick={() => onSelectTask(task.id)}
              className={`
                relative group flex items-center rounded-lg cursor-pointer
                transition-all duration-200 w-full py-1.5
                ${rowLayoutClass}
                ${isActive && !isComplete ? 'bg-primary/10' : isComplete && isActive ? 'bg-emerald-50' : 'hover:bg-gray-100'}
              `}
              role="button"
              tabIndex={0}
              {...collapsedNavProps(`${task.shortTitle}${isNotAdministered ? ' — not administered' : isComplete ? ' — complete' : isStarted ? ' — in progress' : ''}`)}
            >
              {/* Numbered badge — colour reflects completion status */}
              <div className={`
                ${badgeSizeClass} flex items-center justify-center text-xs font-bold flex-shrink-0
                transition-all duration-200
                ${badgeClasses(isActive, isComplete, isStarted, isNotAdministered)}
              `}>
                {displayNumber}
              </div>

              {/* Task title — visible when expanded */}
              <span className={`
                text-sm font-medium truncate whitespace-nowrap
                transition-opacity duration-200
                ${isNotAdministered ? 'text-gray-400 italic' : isComplete ? 'text-emerald-700 font-semibold' : isActive ? 'text-gray-900 font-semibold' : 'text-gray-600'}
                ${labelVisibilityClass}
              `}>
                {task.shortTitle}
              </span>

              {/* Remove button — opens confirmation dialog (hidden in preset mode) */}
              {isExpanded && !isPreset && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmRemoveTaskId(task.id);
                  }}
                  className="
                    ml-auto flex-shrink-0 w-5 h-5 rounded-full
                    flex items-center justify-center
                    text-gray-300 hover:bg-red-100 hover:text-red-500
                    opacity-0 group-hover:opacity-100 transition-all duration-150
                  "
                  title="Remove from assessment"
                  aria-label={`Remove ${task.shortTitle}`}
                >
                  <XIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}

        {/* "Add task" button — only shown in Build Your Own mode */}
        {!isPreset && (
          <div
            onClick={onOpenPicker}
            className={`
              flex items-center rounded-lg cursor-pointer
              transition-all duration-200 w-full py-1.5
              ${rowLayoutClass}
              ${isPickerActive ? 'bg-primary/10' : 'hover:bg-gray-100'}
            `}
            role="button"
            tabIndex={0}
            {...collapsedNavProps('Add a task')}
          >
            <div className={`
              ${badgeSizeClass} flex items-center justify-center flex-shrink-0
              transition-all duration-200 border border-dashed
              ${isPickerActive
                ? 'border-primary text-primary'
                : 'border-gray-300 text-gray-400 hover:border-primary hover:text-primary'
              }
            `}>
              <PlusIcon className="w-3.5 h-3.5" />
            </div>

            <span className={`
              text-sm font-medium truncate whitespace-nowrap
              transition-opacity duration-200
              ${isPickerActive ? 'text-primary' : 'text-gray-500'}
              ${labelVisibilityClass}
            `}>
              Add task
            </span>
          </div>
        )}

        {/* Divider — separates tasks from workflow action buttons */}
        {(onToggleAdosMode || onToggleCriterionMapping) && (
          <div className={`border-t border-gray-200 my-1 transition-all duration-300 ${isExpanded ? 'w-full' : 'w-8 mx-auto'}`} />
        )}

        {/* ADOS-Informed Coding button — advances to the ADOS scoring workflow */}
        {onToggleAdosMode && (
          <div
            onClick={onToggleAdosMode}
            className={`
              flex items-center rounded-lg cursor-pointer
              transition-all duration-200 w-full py-1.5
              ${rowLayoutClass}
              ${adosMode ? 'bg-indigo-50' : 'hover:bg-gray-100'}
            `}
            role="button"
            tabIndex={0}
            {...collapsedNavProps(adosMode ? 'Return to observation tasks' : 'ADOS-Informed Coding')}
          >
            <div className={`
              ${badgeSizeClass} flex items-center justify-center flex-shrink-0
              transition-all duration-200
              ${adosMode
                ? 'bg-indigo-600 text-white ring-2 ring-indigo-400 ring-offset-1'
                : 'bg-gray-200 text-gray-500 hover:bg-indigo-100 hover:text-indigo-600'}
            `}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>

            <span className={`
              text-sm font-medium truncate whitespace-nowrap
              transition-opacity duration-200
              ${adosMode ? 'text-indigo-700 font-semibold' : 'text-gray-600'}
              ${labelVisibilityClass}
            `}>
              ADOS-Informed Coding
            </span>
          </div>
        )}

        {/* Criterion Mapping toggle button */}
        {onToggleCriterionMapping && (
          <div
            onClick={onToggleCriterionMapping}
            className={`
              flex items-center rounded-lg cursor-pointer
              transition-all duration-200 w-full py-1.5
              ${rowLayoutClass}
              ${criterionMappingMode ? 'bg-amber-50' : 'hover:bg-gray-100'}
            `}
            role="button"
            tabIndex={0}
            {...collapsedNavProps(criterionMappingMode ? 'Return to observation mode' : 'Map observation notes to DSM criteria')}
          >
            <div className={`
              ${badgeSizeClass} flex items-center justify-center flex-shrink-0
              transition-all duration-200
              ${criterionMappingMode
                ? 'bg-amber-500 text-white ring-2 ring-amber-400 ring-offset-1'
                : 'bg-gray-200 text-gray-500 hover:bg-primary/10 hover:text-primary'}
            `}>
              {criterionMappingMode ? (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              )}
            </div>

            <span className={`
              text-sm font-medium truncate whitespace-nowrap
              transition-opacity duration-200
              ${criterionMappingMode ? 'text-amber-700 font-semibold' : 'text-gray-600'}
              ${labelVisibilityClass}
            `}>
              {criterionMappingMode ? 'Back to Observation' : 'Map to Criteria'}
            </span>
          </div>
        )}

        {/* Divider — separates action buttons from General Observations */}
        {onSelectGeneral && (
          <div className={`border-t border-gray-200 my-1 transition-all duration-300 ${isExpanded ? 'w-full' : 'w-8 mx-auto'}`} />
        )}

        {/* ── General Observations sub-items (no section title — matches
            Task Administration / Criterion Mapping: badges + labels only) ── */}
        {onSelectGeneral && (
        <>
        {generalPanels.map((panel) => {
          const isActive = activeView === 'general' && activeGeneralPanel === panel.id;
          const isDisabled = !panel.available;

          // Determine completion: Sensory is complete when a B4 evidence
          // decision exists; Self-reported is complete when any notes exist.
          const panelData = allTaskData[`general_${panel.id}`];
          const isComplete = panel.id === 'sensory'
            ? !!panelData?.dsm_B4_evidence
            : Array.isArray(panelData?.notes) && panelData.notes.length > 0;

          return (
            <div
              key={panel.id}
              onClick={() => !isDisabled && onSelectGeneral?.(panel.id)}
              className={`
                flex items-center rounded-lg
                transition-all duration-200 w-full py-1.5
                ${rowLayoutClass}
                ${isDisabled
                  ? 'opacity-40 cursor-not-allowed'
                  : isActive && !isComplete
                    ? 'bg-primary/10 cursor-pointer'
                    : isComplete && isActive
                      ? 'bg-emerald-50 cursor-pointer'
                      : 'hover:bg-gray-100 cursor-pointer'}
              `}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              {...collapsedNavProps(isDisabled ? `${panel.title} — coming soon` : `${panel.title}${isComplete ? ' — complete' : ''}`)}
            >
              <div className={`
                ${badgeSizeClass} flex items-center justify-center flex-shrink-0
                transition-all duration-200
                ${isComplete
                  ? 'bg-emerald-500 text-white'
                  : isActive
                    ? 'bg-primary text-white ring-2 ring-primary ring-offset-1'
                    : 'bg-gray-200 text-gray-500'}
              `}>
                {panel.id === 'self_reported' ? (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </div>

              <span className={`
                text-sm font-medium truncate whitespace-nowrap
                transition-opacity duration-200
                ${isComplete ? 'text-emerald-700 font-semibold' : isActive ? 'text-gray-900 font-semibold' : 'text-gray-600'}
                ${labelVisibilityClass}
              `}>
                {panel.title}
              </span>
            </div>
          );
        })}
        </>
        )}

        {/* Divider — separates General Observations from Review */}
        {shouldShowReview && (
          <div className={`border-t border-gray-200 my-1 transition-all duration-300 ${isExpanded ? 'w-full' : 'w-8 mx-auto'}`} />
        )}

        {/* Review badge — hidden on Task Administration (footer CTA used instead) */}
        {shouldShowReview && (
        <div
          onClick={onSelectSummary}
          className={`
            flex items-center rounded-lg cursor-pointer
            transition-all duration-200 w-full py-1.5
            ${rowLayoutClass}
            ${isSummaryActive ? 'bg-primary/10' : 'hover:bg-gray-100'}
          `}
          role="button"
          tabIndex={0}
          {...collapsedNavProps('Review')}
        >
          <div className={`
            ${badgeSizeClass} flex items-center justify-center flex-shrink-0
            transition-all duration-200
            ${isSummaryActive
              ? 'bg-primary text-white ring-2 ring-primary ring-offset-1'
              : 'bg-gray-200 text-gray-500'
            }
          `}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>

          <span className={`
            text-sm font-medium truncate whitespace-nowrap
            transition-opacity duration-200
            ${isSummaryActive ? 'text-gray-900 font-semibold' : 'text-gray-600'}
            ${labelVisibilityClass}
          `}>
            Review
          </span>
        </div>
        )}
        </>
        )}
        </div>
        {/* Portaled name balloon for collapsed badges — same pattern as SSISideNavbar. */}
        {!isExpanded && hasTasks && (
          <Tooltip
            id={OBS_SIDEBAR_TAB_TOOLTIP_ID}
            place="right"
            className="z-[60] text-xs font-semibold"
            style={{
              backgroundColor: 'rgba(34, 34, 34, 1)',
              borderRadius: '10px',
              color: '#fff',
              fontWeight: 'bold',
            }}
          />
        )}
      </div>

      {/* ── Confirmation dialog for task removal ──────────────────────── */}
      {confirmRemoveTaskId !== null && pendingTask && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setConfirmRemoveTaskId(null)}
        >
          {/* Blurred overlay */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

          {/* Dialog card */}
          <div
            className="
              relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4
              p-6 text-left animate-[fadeScaleIn_200ms_ease-out]
            "
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning icon */}
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <ExclamationIcon className="w-5 h-5 text-red-500" />
            </div>

            <h3 className="text-base font-bold text-gray-900 mb-1">
              Remove task?
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Are you sure you want to remove{' '}
              <span className="font-semibold text-gray-700">{pendingTask.shortTitle}</span>{' '}
              from this assessment? Any observations recorded for this task will be lost.
            </p>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setConfirmRemoveTaskId(null)}
                className="
                  px-4 py-2 text-sm font-medium text-gray-700
                  bg-gray-100 hover:bg-gray-200 rounded-lg
                  transition-colors duration-150
                "
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onRemoveTask(confirmRemoveTaskId);
                  setConfirmRemoveTaskId(null);
                }}
                className="
                  px-4 py-2 text-sm font-medium text-white
                  bg-red-500 hover:bg-red-600 rounded-lg
                  transition-colors duration-150
                "
              >
                Remove task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyframe for dialog entrance animation */}
      <style>{`
        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
